import { useState, useRef, useEffect } from 'react';
import {
  ArrowLeft, Upload, Loader2, Trash2, Save,
  ArrowRight, FileText, CheckCircle2, Plus, X, Copy, AlertTriangle,
} from 'lucide-react';
import { AMBER, NAVY, BORDER_COLOR, bubbleStyle, fieldStyle, selectStyle, navyBtn } from '../../styles/tokens';
import { PAYMENT_METHODS } from '@shared/constants';
import { useOptions } from '../../context/OptionsContext';
import AuthedReceiptPreview from '../../shared/components/AuthedReceiptPreview';
import { createPreviewUrl, revokePreviewUrl } from '../../utils/fileUtils';
import client from '../../api/client';

function genId() { return Math.random().toString(36).slice(2, 10); }

function fmt(amount) {
  const n = parseFloat(amount);
  if (isNaN(n)) return '';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function totalAmount(items) {
  if (!items || items.length === 0) return 0;
  return items.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0);
}

async function analyzeFile(file) {
  try {
    const form = new FormData();
    form.append('file', file);
    const res = await client.post('/api/analyze-receipt', form);
    return res.data;
  } catch (err) { console.error('AI analysis failed:', err); return null; }
}

// ---- Upload Slot ----
function UploadSlot({ slotId, onFile }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) onFile(slotId, f); }}
      onClick={() => inputRef.current?.click()}
      style={{
        ...bubbleStyle,
        border: `2px dashed ${dragOver ? AMBER : BORDER_COLOR}`,
        background: dragOver ? '#fffbeb' : '#fafafa',
        padding: '28px 24px', textAlign: 'center',
        cursor: 'pointer', transition: 'all 0.15s',
      }}
    >
      <Upload size={28} style={{ color: dragOver ? AMBER : '#94a3b8', marginBottom: 8 }} />
      <div style={{ fontWeight: '700', color: NAVY, fontSize: 14, marginBottom: 2 }}>
        Toca para subir un nuevo recibo
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8' }}>PNG, JPG o PDF</div>
      <input ref={inputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files[0]; if (f) onFile(slotId, f); e.target.value = ''; }} />
    </div>
  );
}

// ---- Item Row ----
function ItemRow({ item, index, supervisors, onChange, onRemove, canRemove }) {
  function update(field, value) {
    onChange({ ...item, [field]: value });
  }

  return (
    <tr style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
      <td style={{ padding: '6px 6px', fontSize: 13 }}>
        <input style={{ ...fieldStyle, minWidth: 120 }} value={item.description}
          onChange={(e) => update('description', e.target.value)} placeholder="Descripción" />
      </td>
      <td style={{ padding: '6px 6px', fontSize: 13 }}>
        <input style={{ ...fieldStyle, width: 100 }} value={item.amount}
          onChange={(e) => update('amount', e.target.value)}
          placeholder="0.00" type="number" min="0" step="0.01" />
      </td>
      <td style={{ padding: '6px 6px', fontSize: 13 }}>
        <select style={{ ...selectStyle, minWidth: 140 }} value={item.supervisorId || ''}
          onChange={(e) => {
            const sup = supervisors.find((s) => s.id === Number(e.target.value));
            onChange({
              ...item,
              supervisorId: sup ? sup.id : '',
              supervisorName: sup ? sup.name : '',
            });
          }}>
          <option value="">-- Supervisor --</option>
          {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </td>
      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
        {canRemove && (
          <button onClick={onRemove}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#ef4444', padding: 4, borderRadius: 4,
            }}
            title="Eliminar línea">
            <X size={14} />
          </button>
        )}
      </td>
    </tr>
  );
}

// ---- Receipt Card ----
function ReceiptCard({ card, onChangeField, onChangeItem, onAddItem, onRemoveItem,
  onApplySupervisorToAll, onDelete, onSubmitDraft, onSaveNew, onSubmitNew }) {
  const { supervisors } = useOptions();
  const isDraft = card.type === 'draft';
  const isNew   = card.type === 'new';
  const preview = card.localPreview || card.previewUrl;
  const total   = totalAmount(card.items);

  return (
    <div style={{ ...bubbleStyle, padding: '16px 18px' }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* Preview */}
        <div style={{
          width: 48, height: 48, borderRadius: 8, overflow: 'hidden',
          flexShrink: 0, background: '#f1f5f9', border: `1px solid ${BORDER_COLOR}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {preview && card.fileType?.startsWith('image/') ? (
            card.localPreview ? (
              // Just-selected local file: a blob: URL needs no auth.
              <img src={card.localPreview} alt="receipt" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              // Server-stored image: fetch through the authenticated proxy.
              <AuthedReceiptPreview src={card.previewUrl} fileType={card.fileType} alt="receipt" imgStyle={{ width: '100%', height: '100%', objectFit: 'cover' }} style={{ width: '100%', height: '100%' }} />
            )
          ) : (
            <FileText size={22} style={{ color: '#94a3b8' }} />
          )}
        </div>

        {/* Fields */}
        <div style={{ flex: 1, minWidth: 200 }}>
          {card.analyzing ? (
            <div style={{ fontSize: 13, color: '#94a3b8', padding: '14px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Loader2 size={14} className="spin" /> Analizando recibo...
            </div>
          ) : (
            <>
              {card.analyzeFailed && (
                <div style={{
                  fontSize: 12, color: '#92400e',
                  background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6,
                  padding: '8px 10px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                  No se pudo analizar el recibo automáticamente. Complete los campos manualmente.
                </div>
              )}
              {/* Receipt-level fields: vendor, date, payment method */}
              <div className="receipt-form-grid"
                style={{ display: 'grid', gridTemplateColumns: '1fr 140px 180px', gap: 8, marginBottom: 10 }}>
                <input style={fieldStyle} value={card.vendor}
                  onChange={(e) => onChangeField(card.id, 'vendor', e.target.value)}
                  placeholder="Nombre del proveedor" />
                <input style={fieldStyle} value={card.date}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => onChangeField(card.id, 'date', e.target.value)} type="date" />
                <select style={selectStyle} value={card.paymentMethod}
                  onChange={(e) => onChangeField(card.id, 'paymentMethod', e.target.value)}>
                  <option value="">-- Metodo de Pago --</option>
                  {Object.entries(PAYMENT_METHODS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              {/* AI confidence note */}
              {card.aiSuggested && card.aiConfidence && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6, fontStyle: 'italic' }}>
                  IA: {card.aiConfidence} confianza
                </div>
              )}

              {/* Items sub-table */}
              <div style={{
                border: `1px solid ${BORDER_COLOR}`, borderRadius: 8,
                overflow: 'hidden', marginBottom: 8,
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: `1px solid ${BORDER_COLOR}` }}>
                      <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: '600', color: NAVY, fontSize: 12 }}>Descripcion</th>
                      <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: '600', color: NAVY, fontSize: 12, width: 110 }}>Monto</th>
                      <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: '600', color: NAVY, fontSize: 12, minWidth: 140 }}>Supervisor</th>
                      <th style={{ padding: '6px 6px', width: 32 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(card.items || []).map((lineItem, idx) => (
                      <ItemRow
                        key={lineItem.id}
                        item={lineItem}
                        index={idx}
                        supervisors={supervisors}
                        onChange={(updated) => onChangeItem(card.id, lineItem.id, updated)}
                        onRemove={() => onRemoveItem(card.id, lineItem.id)}
                        canRemove={(card.items || []).length > 1}
                      />
                    ))}
                  </tbody>
                </table>

                {/* Add item + Apply same supervisor */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '6px 8px',
                  background: '#f8fafc', borderTop: `1px solid ${BORDER_COLOR}`,
                }}>
                  <button onClick={() => onAddItem(card.id)}
                    style={{
                      background: 'transparent', border: `1px dashed ${BORDER_COLOR}`,
                      borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: '600',
                      color: '#64748b', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                    <Plus size={12} /> Agregar linea
                  </button>

                  {(card.items || []).length > 1 && (
                    <button onClick={() => onApplySupervisorToAll(card.id)}
                      style={{
                        background: 'transparent', border: `1px dashed ${BORDER_COLOR}`,
                        borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: '600',
                        color: '#64748b', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                      <Copy size={12} /> Aplicar mismo supervisor a todos
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="card-actions"
          style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0, alignItems: 'flex-end' }}>
          {total > 0 && !card.analyzing && (
            <div style={{ fontWeight: '700', fontSize: 15, color: NAVY, marginBottom: 2 }}>
              {fmt(total)}
            </div>
          )}

          {isDraft && (
            <>
              <button onClick={() => onSubmitDraft(card)} disabled={card.submitting || card.analyzing}
                style={{
                  ...navyBtn, fontSize: 13, padding: '9px 16px', minHeight: 40,
                  display: 'flex', alignItems: 'center', gap: 6,
                  opacity: card.submitting ? 0.6 : 1,
                  cursor: card.submitting ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}>
                {card.submitting
                  ? <><Loader2 size={13} className="spin" /> Enviando...</>
                  : <>Enviar <ArrowRight size={13} /></>
                }
              </button>
              <button onClick={() => onDelete(card)} disabled={card.submitting}
                style={{
                  background: 'transparent', border: '1px solid #fecaca',
                  color: '#ef4444', borderRadius: 8, padding: '9px 16px',
                  fontSize: 13, fontWeight: '600', cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap',
                  opacity: card.submitting ? 0.4 : 1, minHeight: 40,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <Trash2 size={13} /> Eliminar
              </button>
            </>
          )}

          {isNew && !card.analyzing && (
            <>
              <button onClick={() => onSubmitNew(card)} disabled={card.submitting}
                style={{
                  ...navyBtn, fontSize: 13, padding: '9px 16px', minHeight: 40,
                  display: 'flex', alignItems: 'center', gap: 6,
                  opacity: card.submitting ? 0.6 : 1,
                  cursor: card.submitting ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}>
                {card.submitting
                  ? <><Loader2 size={13} className="spin" /> Enviando...</>
                  : <>Enviar <ArrowRight size={13} /></>
                }
              </button>
              <button onClick={() => onSaveNew(card)} disabled={card.submitting}
                style={{
                  background: '#fff', color: '#475569', border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 8, padding: '9px 16px', fontWeight: '600',
                  fontSize: 13, cursor: card.submitting ? 'not-allowed' : 'pointer',
                  fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap',
                  opacity: card.submitting ? 0.4 : 1, minHeight: 40,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <Save size={13} /> Guardar como Borrador
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Helper: build card-level items from receipt data ----
function buildItemsFromReceipt(receipt) {
  if (receipt.items && receipt.items.length > 0) {
    return receipt.items.map((it) => ({
      id: it.id || genId(),
      description: it.description || '',
      amount: it.amount !== null && it.amount !== undefined ? String(it.amount) : '',
      category: it.category || '',
      supervisorId: it.supervisorId || '',
      supervisorName: it.supervisorName || '',
      status: it.status || 'draft',
      aiSuggested: it.aiSuggested || false,
      aiFields: it.aiFields || {},
    }));
  }
  // Fallback: create a single empty item
  return [{ id: genId(), description: '', amount: '', category: '', supervisorId: '', supervisorName: '', status: 'draft', aiSuggested: false, aiFields: {} }];
}

// ---- DraftsPage ----
export default function DraftsPage({ initialDrafts, onBack, onToast }) {
  const [cards, setCards] = useState(() =>
    initialDrafts.map((r) => ({
      id: genId(), type: 'draft', receiptId: r.id,
      fileName: r.fileName, fileType: r.fileType,
      previewUrl: r.previewUrl, localPreview: null,
      vendor: r.vendor || '',
      date: r.date || '',
      paymentMethod: r.paymentMethod || '',
      aiSuggested: r.aiSuggested, aiConfidence: r.aiConfidence,
      items: buildItemsFromReceipt(r),
      submitting: false, analyzing: false,
    }))
  );

  useEffect(() => {
    return () => { cards.forEach((c) => { if (c.localPreview) revokePreviewUrl(c.localPreview); }); };
  }, []);

  // ---- Card-level field change ----
  function updateCardField(cardId, field, value) {
    setCards((prev) => prev.map((c) => c.id === cardId ? { ...c, [field]: value } : c));
  }

  function setCardProp(cardId, props) {
    setCards((prev) => prev.map((c) => c.id === cardId ? { ...c, ...props } : c));
  }

  // ---- Item-level changes ----
  function updateItem(cardId, itemId, updatedItem) {
    setCards((prev) => prev.map((c) => {
      if (c.id !== cardId) return c;
      return { ...c, items: c.items.map((it) => it.id === itemId ? updatedItem : it) };
    }));
  }

  function addItem(cardId) {
    setCards((prev) => prev.map((c) => {
      if (c.id !== cardId) return c;
      return {
        ...c,
        items: [...c.items, {
          id: genId(), description: '', amount: '', category: '',
          supervisorId: '', supervisorName: '', status: 'draft',
          aiSuggested: false, aiFields: {},
        }],
      };
    }));
  }

  function removeItem(cardId, itemId) {
    setCards((prev) => prev.map((c) => {
      if (c.id !== cardId) return c;
      if (c.items.length <= 1) return c; // keep at least one
      return { ...c, items: c.items.filter((it) => it.id !== itemId) };
    }));
  }

  function applySupervisorToAll(cardId) {
    setCards((prev) => prev.map((c) => {
      if (c.id !== cardId) return c;
      // Find the first item with a supervisor set
      const source = c.items.find((it) => it.supervisorId);
      if (!source) return c;
      return {
        ...c,
        items: c.items.map((it) => ({
          ...it,
          supervisorId: source.supervisorId,
          supervisorName: source.supervisorName,
        })),
      };
    }));
  }

  // ---- Validation ----
  function validateCard(card) {
    const missing = [];
    if (!card.vendor?.trim())   missing.push('Proveedor');
    if (!card.date)             missing.push('Fecha');
    if (!card.paymentMethod)    missing.push('Método de Pago');
    if (!card.receiptTotal || isNaN(parseFloat(card.receiptTotal)) || parseFloat(card.receiptTotal) <= 0)
      missing.push('Total del Recibo');

    // Validate each item
    const itemErrors = [];
    (card.items || []).forEach((it, idx) => {
      const label = card.items.length > 1 ? ` (línea ${idx + 1})` : '';
      if (!it.amount || parseFloat(it.amount) <= 0) itemErrors.push(`Monto${label}`);
      if (!it.description?.trim()) itemErrors.push(`Descripción${label}`);
      if (!it.projectId) itemErrors.push(`Proyecto${label}`);
    });

    const allMissing = [...missing, ...itemErrors];
    if (allMissing.length > 0) {
      alert(`Por favor, completa todos los campos requeridos antes de enviar:\n\u2022 ${allMissing.join('\n\u2022 ')}`);
      return false;
    }
    return true;
  }

  // ---- Build items payload for the API ----
  function buildItemsPayload(card) {
    return (card.items || []).map((it) => ({
      id: it.id,
      description: it.description,
      amount: it.amount,
      supervisorId: it.supervisorId || null,
      supervisorName: it.supervisorName || null,
      status: it.status || 'draft',
      aiSuggested: it.aiSuggested || false,
      aiFields: it.aiFields || {},
    }));
  }

  // ---- Delete ----
  async function handleDelete(card) {
    setCardProp(card.id, { submitting: true });
    try {
      await client.delete(`/api/receipts/${card.receiptId}`);
      setCards((prev) => prev.map((c) => c.id === card.id ? { type: 'upload', id: genId() } : c));
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error al eliminar el recibo.');
      setCardProp(card.id, { submitting: false });
    }
  }

  // ---- Upload to slot ----
  async function handleFileUpload(slotId, file) {
    const localPreview = createPreviewUrl(file);
    const newCard = {
      id: slotId, type: 'new', file, fileName: file.name, fileType: file.type,
      localPreview, previewUrl: null,
      vendor: '', date: '', paymentMethod: '',
      items: [{ id: genId(), description: '', amount: '', category: '', supervisorId: '', supervisorName: '', status: 'draft', aiSuggested: false, aiFields: {} }],
      aiSuggested: false, aiConfidence: null,
      analyzing: true, submitting: false,
    };
    setCards((prev) => prev.map((c) => c.id === slotId ? newCard : c));

    const result = await analyzeFile(file);

    setCards((prev) =>
      prev.map((c) => {
        if (c.id !== slotId) return c;
        if (!result) {
          return { ...c, analyzing: false, analyzeFailed: true };
        }
        // New AI response format: { vendor, date, confidence, items: [{description, amount, suggestedCategory}] }
        const aiItems = (result.items && result.items.length > 0)
          ? result.items.map((ai) => ({
              id: genId(),
              description: ai.description || '',
              amount: ai.amount !== null && ai.amount !== undefined ? String(ai.amount) : '',
              category: ai.suggestedCategory || '',
              supervisorId: '',
              supervisorName: '',
              status: 'draft',
              aiSuggested: true,
              aiFields: {
                description: !!ai.description,
                amount: ai.amount !== null && ai.amount !== undefined,
                category: !!ai.suggestedCategory,
              },
            }))
          : [{ id: genId(), description: '', amount: '', category: '', supervisorId: '', supervisorName: '', status: 'draft', aiSuggested: false, aiFields: {} }];

        return {
          ...c,
          analyzing: false,
          analyzeFailed: false,
          vendor: result.vendor || '',
          date: result.date || '',
          aiSuggested: true,
          aiConfidence: result.confidence || null,
          items: aiItems,
        };
      })
    );
  }

  // ---- Submit draft ----
  async function handleSubmitDraft(card) {
    if (!validateCard(card)) return;
    setCardProp(card.id, { submitting: true });
    try {
      await client.patch(`/api/receipts/${card.receiptId}`, {
        vendor: card.vendor,
        date: card.date,
        paymentMethod: card.paymentMethod,
        status: 'pending',
        items: buildItemsPayload(card),
      });
      setCards((prev) => prev.filter((c) => c.id !== card.id));
      onToast('submitted', 'Recibo enviado para revision');
    } catch (err) {
      console.error('Submit draft error:', err);
      alert(err.response?.data?.error || 'Error al enviar.');
      setCardProp(card.id, { submitting: false });
    }
  }

  // ---- Save new as draft ----
  async function handleSaveNew(card) {
    setCardProp(card.id, { submitting: true });
    try {
      const form = new FormData();
      form.append('file', card.file);
      form.append('vendor', card.vendor);
      form.append('date', card.date);
      form.append('paymentMethod', card.paymentMethod);
      form.append('status', 'draft');
      form.append('aiSuggested', String(card.aiSuggested));
      form.append('aiConfidence', card.aiConfidence || '');
      form.append('items', JSON.stringify(buildItemsPayload(card)));

      const res = await client.post('/api/receipts', form);
      const saved = res.data;
      if (card.localPreview) revokePreviewUrl(card.localPreview);

      setCards((prev) =>
        prev.map((c) => c.id === card.id ? {
          id: card.id, type: 'draft', receiptId: saved.id,
          fileName: saved.fileName, fileType: saved.fileType,
          previewUrl: saved.previewUrl, localPreview: null,
          vendor: saved.vendor || '',
          date: saved.date || '',
          paymentMethod: saved.paymentMethod || '',
          aiSuggested: saved.aiSuggested, aiConfidence: saved.aiConfidence,
          items: buildItemsFromReceipt(saved),
          submitting: false, analyzing: false,
        } : c)
      );
      onToast('draft', 'Recibo guardado como borrador');
    } catch (err) {
      console.error('Save new error:', err);
      alert('Error al guardar el borrador.');
      setCardProp(card.id, { submitting: false });
    }
  }

  // ---- Submit new directly ----
  async function handleSubmitNew(card) {
    if (!validateCard(card)) return;
    setCardProp(card.id, { submitting: true });
    try {
      const form = new FormData();
      form.append('file', card.file);
      form.append('vendor', card.vendor);
      form.append('date', card.date);
      form.append('paymentMethod', card.paymentMethod);
      form.append('status', 'pending');
      form.append('aiSuggested', String(card.aiSuggested));
      form.append('aiConfidence', card.aiConfidence || '');
      form.append('items', JSON.stringify(buildItemsPayload(card)));

      await client.post('/api/receipts', form);
      if (card.localPreview) revokePreviewUrl(card.localPreview);
      setCards((prev) => prev.filter((c) => c.id !== card.id));
      onToast('submitted', 'Recibo enviado para revision');
    } catch (err) {
      console.error('Submit new error:', err);
      alert('Error al enviar.');
      setCardProp(card.id, { submitting: false });
    }
  }

  const hasCards = cards.length > 0;

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <button onClick={onBack}
          style={{
            background: 'transparent', border: `1px solid ${BORDER_COLOR}`,
            color: '#475569', borderRadius: 8, padding: '7px 14px',
            fontWeight: '600', fontSize: 13, cursor: 'pointer',
            fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: 6,
          }}>
          <ArrowLeft size={14} /> Regresar
        </button>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: '700', color: NAVY, margin: 0 }}>Recibos en Borrador</h2>
          {hasCards && (
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
              {cards.filter((c) => c.type === 'draft').length} borrador{cards.filter((c) => c.type === 'draft').length !== 1 ? 'es' : ''} — enviar individualmente
            </div>
          )}
        </div>
      </div>

      {/* Empty state */}
      {!hasCards && (
        <div style={{ ...bubbleStyle, textAlign: 'center', padding: '60px 20px' }}>
          <CheckCircle2 size={44} style={{ color: '#86efac', marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: '700', color: NAVY, marginBottom: 6 }}>
            Todos los borradores enviados!
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
            No quedan borradores. Vuelve a tus reportes.
          </div>
          <button onClick={onBack}
            style={{ ...navyBtn, fontSize: 13, padding: '10px 22px', minHeight: 44 }}>
            Volver a Reportes
          </button>
        </div>
      )}

      {hasCards && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cards.map((card) =>
            card.type === 'upload' ? (
              <UploadSlot key={card.id} slotId={card.id} onFile={handleFileUpload} />
            ) : (
              <ReceiptCard key={card.id} card={card}
                onChangeField={updateCardField}
                onChangeItem={updateItem}
                onAddItem={addItem}
                onRemoveItem={removeItem}
                onApplySupervisorToAll={applySupervisorToAll}
                onDelete={handleDelete}
                onSubmitDraft={handleSubmitDraft}
                onSaveNew={handleSaveNew}
                onSubmitNew={handleSubmitNew}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}
